import { supabase } from '../lib/supabase';

export const reportsService = {
  // Get financial overview statistics with enhanced data validation
  async getFinancialOverview(organizationId) {
    try {
      // Get current user for security
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      console.debug('🔍 Starting financial overview for organization:', organizationId);

      // Validate that we have customers with proper names (strict filtering - no templates/defaults)
      const { data: validCustomers, error: customerError } = await supabase
        ?.from('customers')
        ?.select('id, customer_name, status')
        ?.eq('organization_id', organizationId)
        ?.eq('status', 'active') // Only active customers
        ?.not('customer_name', 'is', null)
        ?.neq('customer_name', '')
        ?.neq('customer_name', 'Default')
        ?.neq('customer_name', 'Template')
        ?.neq('customer_name', 'Test')
        ?.neq('customer_name', 'Sample');

      if (customerError) {
        console.error('❌ Customer validation error:', customerError);
        throw customerError;
      }

      console.debug('👥 Valid customers found:', validCustomers?.length, validCustomers);

      if (!validCustomers || validCustomers?.length === 0) {
        throw new Error('No customers with valid names found. Please ensure customer names are properly entered and not using template/default values.');
      }

      // Validate that we have revenue accounts with proper subtypes (strict filtering - no system accounts)
      const { data: revenueAccounts, error: accountError } = await supabase
        ?.from('chart_of_accounts')
        ?.select('id, account_name, account_subtype, account_type, status, is_system_account')
        ?.eq('organization_id', organizationId)
        ?.eq('account_type', 'revenue')
        ?.eq('status', 'active')
        ?.eq('is_system_account', false) // Exclude system accounts
        ?.not('account_subtype', 'is', null)
        ?.not('account_name', 'ilike', '%template%')
        ?.not('account_name', 'ilike', '%default%')
        ?.not('account_name', 'ilike', '%test%')
        ?.not('account_name', 'ilike', '%sample%');

      if (accountError) {
        console.error('❌ Revenue account validation error:', accountError);
        throw accountError;
      }

      console.debug('💰 Valid revenue accounts found:', revenueAccounts?.length, revenueAccounts);

      if (!revenueAccounts || revenueAccounts?.length === 0) {
        throw new Error('No revenue accounts with proper subtypes found. Please ensure account subtypes are configured and not using template/default values.');
      }

      // Revenue (only from invoices with valid customers and actual amounts)
      const { data: invoiceData, error: invoiceError } = await supabase
        ?.from('invoices')
        ?.select(`
          total_amount, 
          status, 
          issue_date, 
          amount_paid,
          customers!inner (
            customer_name,
            status
          )
        `)
        ?.eq('organization_id', organizationId)
        ?.eq('customers.status', 'active') // Only active customers
        ?.not('customers.customer_name', 'is', null)
        ?.neq('customers.customer_name', '')
        ?.neq('customers.customer_name', 'Default')
        ?.neq('customers.customer_name', 'Template')
        ?.neq('customers.customer_name', 'Test')
        ?.neq('customers.customer_name', 'Sample')
        ?.gt('total_amount', 0); // Only invoices with actual amounts

      if (invoiceError) {
        console.error('❌ Invoice validation error:', invoiceError);
        throw invoiceError;
      }

      console.debug('📄 Valid invoices found:', invoiceData?.length, invoiceData);

      if (!invoiceData || invoiceData?.length === 0) {
        throw new Error('No invoices with valid customer data found. Revenue cannot be calculated without proper customer-invoice relationships with actual amounts.');
      }

      // Expenses (total expenses + bills) - these don't require the same validation
      const { data: expenseData } = await supabase?.from('expenses')?.select('amount, status, expense_date')?.eq('organization_id', organizationId);

      const { data: billData } = await supabase?.from('bills')?.select('total_amount, status, issue_date, amount_paid')?.eq('organization_id', organizationId);

      // Payments received (only from valid customers with strict filtering)
      const { data: paymentsReceived } = await supabase
        ?.from('payments_received')?.select(`amount_received, payment_date, status,customers!inner (customer_name,status)`)?.eq('organization_id', organizationId)?.eq('customers.status', 'active')?.not('customers.customer_name', 'is', null)?.neq('customers.customer_name', '')?.neq('customers.customer_name', 'Default')?.neq('customers.customer_name', 'Template')?.neq('customers.customer_name', 'Test')?.neq('customers.customer_name', 'Sample');

      // Payments made  
      const { data: paymentsMade } = await supabase?.from('payments_made')?.select('amount_paid, payment_date, status')?.eq('organization_id', organizationId);

      // Calculate totals with strict validation (no template/default data)
      const totalRevenue = invoiceData?.reduce((sum, inv) => sum + parseFloat(inv?.total_amount || 0), 0) || 0;
      const totalExpenses = (expenseData?.reduce((sum, exp) => sum + parseFloat(exp?.amount || 0), 0) || 0) +
                           (billData?.reduce((sum, bill) => sum + parseFloat(bill?.total_amount || 0), 0) || 0);
      const totalPaymentsReceived = paymentsReceived?.reduce((sum, pay) => sum + parseFloat(pay?.amount_received || 0), 0) || 0;
      const totalPaymentsMade = paymentsMade?.reduce((sum, pay) => sum + parseFloat(pay?.amount_paid || 0), 0) || 0;

      // Outstanding amounts (only from validated invoices)
      const totalOutstanding = invoiceData?.reduce((sum, inv) => {
        if (inv?.status !== 'paid') {
          return sum + (parseFloat(inv?.total_amount || 0) - parseFloat(inv?.amount_paid || 0));
        }
        return sum;
      }, 0) || 0;

      const totalPendingBills = billData?.reduce((sum, bill) => {
        if (bill?.status !== 'paid') {
          return sum + (parseFloat(bill?.total_amount || 0) - parseFloat(bill?.amount_paid || 0));
        }
        return sum;
      }, 0) || 0;

      const result = {
        totalRevenue,
        totalExpenses,
        totalPaymentsReceived,
        totalPaymentsMade,
        totalOutstanding,
        totalPendingBills,
        netIncome: totalRevenue - totalExpenses,
        cashFlow: totalPaymentsReceived - totalPaymentsMade,
        // Enhanced validation metadata
        validationInfo: {
          validCustomersCount: validCustomers?.length,
          revenueAccountsCount: revenueAccounts?.length,
          validInvoicesCount: invoiceData?.length,
          dataValidated: true,
          strictFiltering: true, // Indicates template/default data was filtered out
          actualDataOnly: true
        }
      };

      console.debug('✅ Financial overview result:', result);
      return result;
    } catch (error) {
      console.error('❌ Error fetching financial overview:', error);
      throw error;
    }
  },

  // Get report categories with enhanced validation for revenue data
  async getReportCategories(organizationId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      console.debug('🔍 Validating report categories for organization:', organizationId);

      // Validate prerequisites for revenue reporting (strict filtering)
      const { data: validCustomers } = await supabase
        ?.from('customers')
        ?.select('id')
        ?.eq('organization_id', organizationId)
        ?.eq('status', 'active')
        ?.not('customer_name', 'is', null)
        ?.neq('customer_name', '')
        ?.neq('customer_name', 'Default')
        ?.neq('customer_name', 'Template')
        ?.neq('customer_name', 'Test')
        ?.neq('customer_name', 'Sample');

      const { data: revenueAccounts } = await supabase
        ?.from('chart_of_accounts')
        ?.select('id')
        ?.eq('organization_id', organizationId)
        ?.eq('account_type', 'revenue')
        ?.eq('status', 'active')
        ?.eq('is_system_account', false)
        ?.not('account_subtype', 'is', null)
        ?.not('account_name', 'ilike', '%template%')
        ?.not('account_name', 'ilike', '%default%')
        ?.not('account_name', 'ilike', '%test%');

      // Check for actual invoices with valid amounts
      const { data: validInvoices } = await supabase
        ?.from('invoices')
        ?.select('id')
        ?.eq('organization_id', organizationId)
        ?.gt('total_amount', 0);

      const hasValidRevenueSetup = validCustomers && validCustomers?.length > 0 && 
                                   revenueAccounts && revenueAccounts?.length > 0 && 
                                   validInvoices && validInvoices?.length > 0;

      console.debug('📊 Revenue setup validation:', {
        validCustomers: validCustomers?.length || 0,
        revenueAccounts: revenueAccounts?.length || 0,
        validInvoices: validInvoices?.length || 0,
        hasValidRevenueSetup
      });

      // Count records for each category
      const { count: invoiceCount } = await supabase
        ?.from('invoices')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('organization_id', organizationId)
        ?.gt('total_amount', 0); // Only count invoices with actual amounts

      const { count: billCount } = await supabase?.from('bills')?.select('*', { count: 'exact', head: true })?.eq('organization_id', organizationId);
      const { count: expenseCount } = await supabase?.from('expenses')?.select('*', { count: 'exact', head: true })?.eq('organization_id', organizationId);
      const { count: paymentCount } = await supabase?.from('payments_received')?.select('*', { count: 'exact', head: true })?.eq('organization_id', organizationId);
      const { count: journalCount } = await supabase?.from('journal_entries')?.select('*', { count: 'exact', head: true })?.eq('organization_id', organizationId);

      return [
        {
          id: 'revenue',
          name: 'Revenue Reports',
          description: hasValidRevenueSetup ? 'Income and sales analysis (actual data only)' : 'Revenue data requires valid customer names, account subtypes, and actual invoice amounts',
          icon: 'TrendingUp',
          color: hasValidRevenueSetup ? 'primary' : 'muted',
          reportCount: hasValidRevenueSetup ? (invoiceCount || 0) : 0,
          isEnabled: hasValidRevenueSetup,
          validationMessage: hasValidRevenueSetup ? null : 'Configure actual customer names, revenue account subtypes, and create invoices with amounts first',
          reports: hasValidRevenueSetup ? [
            { id: 'sales-summary', name: 'Sales Summary (Actual Data)', type: 'revenue' },
            { id: 'invoice-aging', name: 'Invoice Aging Report (Actual Data)', type: 'revenue' },
            { id: 'customer-revenue', name: 'Customer Revenue Analysis (Actual Data)', type: 'revenue' }
          ] : []
        },
        {
          id: 'expenses',
          name: 'Expense Reports',
          description: 'Cost and spending analysis',
          icon: 'ArrowDown',
          color: 'error',
          reportCount: (expenseCount || 0) + (billCount || 0),
          isEnabled: true,
          reports: [
            { id: 'expense-summary', name: 'Expense Summary', type: 'expenses' },
            { id: 'expense-categories', name: 'Expense by Category', type: 'expenses' },
            { id: 'vendor-spending', name: 'Vendor Spending Analysis', type: 'expenses' }
          ]
        },
        {
          id: 'cash-flow',
          name: 'Cash Flow Reports',
          description: 'Payment and liquidity analysis',
          icon: 'ArrowRightLeft',
          color: 'success',
          reportCount: paymentCount || 0,
          isEnabled: true,
          reports: [
            { id: 'cash-flow-statement', name: 'Cash Flow Statement', type: 'cash-flow' },
            { id: 'payment-trends', name: 'Payment Trends', type: 'cash-flow' },
            { id: 'outstanding-balances', name: 'Outstanding Balances', type: 'cash-flow' }
          ]
        },
        {
          id: 'financial',
          name: 'Financial Reports',
          description: 'Comprehensive financial statements',
          icon: 'FileText',
          color: 'accent',
          reportCount: journalCount || 0,
          isEnabled: true,
          reports: [
            { id: 'profit-loss', name: 'Profit & Loss Statement', type: 'financial' },
            { id: 'balance-sheet', name: 'Balance Sheet', type: 'financial' },
            { id: 'trial-balance', name: 'Trial Balance', type: 'financial' }
          ]
        }
      ];
    } catch (error) {
      console.error('❌ Error fetching report categories:', error);
      throw error;
    }
  },

  // Get specific report data
  async getReportData(reportId, organizationId, filters = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      switch (reportId) {
        case 'sales-summary':
          return await this.getSalesSummaryData(organizationId, filters);
        case 'expense-summary':
          return await this.getExpenseSummaryData(organizationId, filters);
        case 'cash-flow-statement':
          return await this.getCashFlowData(organizationId, filters);
        case 'outstanding-balances':
          return await this.getOutstandingBalancesData(organizationId, filters);
        case 'trial-balance':
          return await this.getTrialBalanceData(organizationId, filters);
        case 'profit-loss':
          return await this.getProfitLossData(organizationId, filters);
        case 'balance-sheet':
          return await this.getBalanceSheetData(organizationId, filters);
        case 'invoice-aging':
          return await this.getInvoiceAgingData(organizationId, filters);
        case 'customer-revenue':
          return await this.getCustomerRevenueData(organizationId, filters);
        case 'expense-categories':
          return await this.getExpenseCategoriesData(organizationId, filters);
        case 'vendor-spending':
          return await this.getVendorSpendingData(organizationId, filters);
        case 'payment-trends':
          return await this.getPaymentTrendsData(organizationId, filters);
        default:
          // Instead of throwing an error, return a placeholder response for unknown reports
          console.warn(`Report type '${reportId}' not implemented yet`);
          return {
            title: 'Report Not Available',
            reportId: reportId,
            message: `The report "${reportId}" is currently being developed and will be available soon.`,
            data: [],
            totals: {},
            isPlaceholder: true
          };
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
      throw error;
    }
  },

  async getSalesSummaryData(organizationId, filters) {
    // Enhanced validation with strict filtering for customer names and account subtypes
    const { data: validCustomers } = await supabase
      ?.from('customers')
      ?.select('id, customer_name')
      ?.eq('organization_id', organizationId)
      ?.eq('status', 'active')
      ?.not('customer_name', 'is', null)
      ?.neq('customer_name', '')
      ?.neq('customer_name', 'Default')
      ?.neq('customer_name', 'Template')
      ?.neq('customer_name', 'Test')
      ?.neq('customer_name', 'Sample');

    if (!validCustomers || validCustomers?.length === 0) {
      throw new Error('Cannot generate sales summary: No customers with valid names found (excluding template/default values)');
    }

    const { data: revenueAccounts } = await supabase
      ?.from('chart_of_accounts')
      ?.select('id')
      ?.eq('organization_id', organizationId)
      ?.eq('account_type', 'revenue')
      ?.eq('status', 'active')
      ?.eq('is_system_account', false)
      ?.not('account_subtype', 'is', null)
      ?.not('account_name', 'ilike', '%template%')
      ?.not('account_name', 'ilike', '%default%')
      ?.not('account_name', 'ilike', '%test%');

    if (!revenueAccounts || revenueAccounts?.length === 0) {
      throw new Error('Cannot generate sales summary: No revenue accounts with proper subtypes found (excluding template/default values)');
    }

    // Only get invoices with valid customers and actual amounts
    const { data, error } = await supabase
      ?.from('invoices')
      ?.select(`
        *,
        customers!inner (
          customer_name,
          customer_number
        )
      `)
      ?.eq('organization_id', organizationId)
      ?.eq('customers.status', 'active')
      ?.not('customers.customer_name', 'is', null)
      ?.neq('customers.customer_name', '')
      ?.neq('customers.customer_name', 'Default')
      ?.neq('customers.customer_name', 'Template')
      ?.neq('customers.customer_name', 'Test')
      ?.neq('customers.customer_name', 'Sample')
      ?.gt('total_amount', 0)
      ?.order('issue_date', { ascending: false });

    if (error) throw error;

    if (!data || data?.length === 0) {
      return {
        title: 'Sales Summary Report (Actual Data Only)',
        data: [],
        totals: {
          totalSales: 0,
          totalPaid: 0,
          totalOutstanding: 0
        },
        validationMessage: 'No sales data available with valid customer names, amounts, and account configurations (excluding template/default values)'
      };
    }

    return {
      title: 'Sales Summary Report (Actual Data Only - No Templates)',
      data: data?.map(invoice => ({
        invoiceNumber: invoice?.invoice_number,
        customerName: invoice?.customers?.customer_name || 'Unknown Customer',
        issueDate: invoice?.issue_date,
        dueDate: invoice?.due_date,
        totalAmount: parseFloat(invoice?.total_amount || 0),
        amountPaid: parseFloat(invoice?.amount_paid || 0),
        balanceDue: parseFloat(invoice?.total_amount || 0) - parseFloat(invoice?.amount_paid || 0),
        status: invoice?.status
      })) || [],
      totals: {
        totalSales: data?.reduce((sum, inv) => sum + parseFloat(inv?.total_amount || 0), 0) || 0,
        totalPaid: data?.reduce((sum, inv) => sum + parseFloat(inv?.amount_paid || 0), 0) || 0,
        totalOutstanding: data?.reduce((sum, inv) => {
          return sum + (parseFloat(inv?.total_amount || 0) - parseFloat(inv?.amount_paid || 0));
        }, 0) || 0
      },
      validationInfo: {
        validCustomersUsed: validCustomers?.length,
        validInvoicesIncluded: data?.length,
        dataSource: 'validated_customer_invoice_relationships_actual_data_only',
        strictFiltering: true
      }
    };
  },

  async getExpenseSummaryData(organizationId, filters) {
    const { data: expenses, error: expError } = await supabase?.from('expenses')?.select(`
        *,
        vendors (
          vendor_name,
          vendor_number
        )
      `)?.eq('organization_id', organizationId)?.order('expense_date', { ascending: false });

    const { data: bills, error: billError } = await supabase?.from('bills')?.select(`
        *,
        vendors (
          vendor_name,
          vendor_number
        )
      `)?.eq('organization_id', organizationId)?.order('issue_date', { ascending: false });

    if (expError) throw expError;
    if (billError) throw billError;

    const expenseData = expenses?.map(expense => ({
      type: 'Expense',
      number: expense?.expense_number,
      vendorName: expense?.vendors?.vendor_name || 'Unknown Vendor',
      date: expense?.expense_date,
      amount: parseFloat(expense?.amount || 0),
      category: expense?.category,
      status: expense?.status,
      description: expense?.description
    })) || [];

    const billData = bills?.map(bill => ({
      type: 'Bill',
      number: bill?.bill_number,
      vendorName: bill?.vendors?.vendor_name || 'Unknown Vendor',
      date: bill?.issue_date,
      amount: parseFloat(bill?.total_amount || 0),
      category: 'Bill Payment',
      status: bill?.status,
      description: `Bill from ${bill?.vendors?.vendor_name || 'Vendor'}`
    })) || [];

    const allExpenseData = [...expenseData, ...billData]?.sort((a, b) => new Date(b.date) - new Date(a.date));

    return {
      title: 'Expense Summary Report',
      data: allExpenseData,
      totals: {
        totalExpenses: allExpenseData?.reduce((sum, exp) => sum + exp?.amount, 0),
        totalByCategory: allExpenseData?.reduce((acc, exp) => {
          acc[exp.category] = (acc?.[exp?.category] || 0) + exp?.amount;
          return acc;
        }, {})
      }
    };
  },

  async getCashFlowData(organizationId, filters) {
    const { data: inflows, error: inflowError } = await supabase?.from('payments_received')?.select(`
        *,
        customers (
          customer_name
        )
      `)?.eq('organization_id', organizationId)?.order('payment_date', { ascending: false });

    const { data: outflows, error: outflowError } = await supabase?.from('payments_made')?.select(`
        *,
        vendors (
          vendor_name
        )
      `)?.eq('organization_id', organizationId)?.order('payment_date', { ascending: false });

    if (inflowError) throw inflowError;
    if (outflowError) throw outflowError;

    return {
      title: 'Cash Flow Statement',
      inflows: inflows?.map(payment => ({
        paymentReference: payment?.payment_reference,
        customerName: payment?.customers?.customer_name || 'Unknown Customer',
        date: payment?.payment_date,
        amount: parseFloat(payment?.amount_received || 0),
        method: payment?.payment_method,
        status: payment?.status
      })) || [],
      outflows: outflows?.map(payment => ({
        paymentReference: payment?.payment_reference,
        vendorName: payment?.vendors?.vendor_name || 'Unknown Vendor',
        date: payment?.payment_date,
        amount: parseFloat(payment?.amount_paid || 0),
        method: payment?.payment_method,
        status: payment?.status
      })) || [],
      summary: {
        totalInflows: inflows?.reduce((sum, pay) => sum + parseFloat(pay?.amount_received || 0), 0) || 0,
        totalOutflows: outflows?.reduce((sum, pay) => sum + parseFloat(pay?.amount_paid || 0), 0) || 0,
        netCashFlow: (inflows?.reduce((sum, pay) => sum + parseFloat(pay?.amount_received || 0), 0) || 0) - 
                     (outflows?.reduce((sum, pay) => sum + parseFloat(pay?.amount_paid || 0), 0) || 0)
      }
    };
  },

  async getOutstandingBalancesData(organizationId, filters) {
    const { data: invoices, error } = await supabase?.from('invoices')?.select(`
        *,
        customers (
          customer_name,
          customer_number,
          email
        )
      `)?.eq('organization_id', organizationId)?.neq('status', 'paid')?.order('due_date', { ascending: true });

    if (error) throw error;

    const outstandingData = invoices?.map(invoice => {
      const totalAmount = parseFloat(invoice?.total_amount || 0);
      const amountPaid = parseFloat(invoice?.amount_paid || 0);
      const balanceDue = totalAmount - amountPaid;
      const daysOverdue = invoice?.due_date 
        ? Math.max(0, Math.floor((new Date() - new Date(invoice.due_date)) / (1000 * 60 * 60 * 24)))
        : 0;

      return {
        invoiceNumber: invoice?.invoice_number,
        customerName: invoice?.customers?.customer_name || 'Unknown Customer',
        customerEmail: invoice?.customers?.email || '',
        issueDate: invoice?.issue_date,
        dueDate: invoice?.due_date,
        totalAmount,
        amountPaid,
        balanceDue,
        daysOverdue,
        status: invoice?.status,
        agingCategory: daysOverdue === 0 ? 'Current' :
                      daysOverdue <= 30 ? '1-30 Days' :
                      daysOverdue <= 60 ? '31-60 Days' :
                      daysOverdue <= 90 ? '61-90 Days' : '90+ Days'
      };
    })?.filter(invoice => invoice?.balanceDue > 0) || [];

    return {
      title: 'Outstanding Balances Report',
      data: outstandingData,
      aging: {
        current: outstandingData?.filter(inv => inv?.daysOverdue === 0)?.reduce((sum, inv) => sum + inv?.balanceDue, 0),
        days1to30: outstandingData?.filter(inv => inv?.daysOverdue > 0 && inv?.daysOverdue <= 30)?.reduce((sum, inv) => sum + inv?.balanceDue, 0),
        days31to60: outstandingData?.filter(inv => inv?.daysOverdue > 30 && inv?.daysOverdue <= 60)?.reduce((sum, inv) => sum + inv?.balanceDue, 0),
        days61to90: outstandingData?.filter(inv => inv?.daysOverdue > 60 && inv?.daysOverdue <= 90)?.reduce((sum, inv) => sum + inv?.balanceDue, 0),
        days90plus: outstandingData?.filter(inv => inv?.daysOverdue > 90)?.reduce((sum, inv) => sum + inv?.balanceDue, 0)
      },
      totals: {
        totalOutstanding: outstandingData?.reduce((sum, inv) => sum + inv?.balanceDue, 0),
        totalInvoices: outstandingData?.length
      }
    };
  },

  async getTrialBalanceData(organizationId, filters) {
    try {
      const asOfDate = filters?.asOfDate || new Date()?.toISOString()?.split('T')?.[0];
      const useJournalCalculations = filters?.useJournalCalculations !== false; // Default to true

      // Call the enhanced database function to generate trial balance with expense integration
      const { data, error } = await supabase?.rpc('generate_trial_balance', {
        org_uuid: organizationId,
        as_of_date: asOfDate,
        use_journal_calculations: useJournalCalculations
      });

      if (error) throw error;

      // Get enhanced trial balance summary statistics with expense details
      const { data: summaryData, error: summaryError } = await supabase?.rpc('get_trial_balance_summary', {
        org_uuid: organizationId,
        as_of_date: asOfDate
      });

      if (summaryError) {
        console.warn('Could not load enhanced trial balance summary:', summaryError);
      }

      // Get detailed expense account breakdown
      const { data: expenseDetails, error: expenseError } = await supabase?.rpc('get_trial_balance_expense_details', {
        org_uuid: organizationId,
        as_of_date: asOfDate
      });

      if (expenseError) {
        console.warn('Could not load expense account details:', expenseError);
      }

      // Get expense account validation information
      const { data: validationData, error: validationError } = await supabase?.rpc('validate_expense_account_integration', {
        org_uuid: organizationId
      });

      if (validationError) {
        console.warn('Could not load expense validation data:', validationError);
      }

      const summary = summaryData?.[0] || {
        total_debit_balance: 0,
        total_credit_balance: 0,
        balance_difference: 0,
        is_balanced: false,
        total_accounts: 0,
        accounts_with_activity: 0,
        total_expense_accounts: 0,
        total_expense_amount: 0,
        total_bill_accounts: 0,
        total_bill_amount: 0,
        data_integration_status: 'basic'
      };

      const validation = validationData?.[0] || {
        total_expenses_in_table: 0,
        expenses_with_account_id: 0,
        expenses_without_account_id: 0,
        suggested_actions: 'No validation data available'
      };

      // Calculate totals from enhanced data
      let totalDebits = data?.reduce((sum, account) => sum + parseFloat(account?.debit_balance || 0), 0) || 0;
      let totalCredits = data?.reduce((sum, account) => sum + parseFloat(account?.credit_balance || 0), 0) || 0;
      const totalNetActivity = data?.reduce((sum, account) => sum + parseFloat(account?.period_activity || 0), 0) || 0;

      // Group accounts by type for better presentation
      const accountsByType = data?.reduce((acc, account) => {
        const type = account?.account_type || 'Other';
        if (!acc?.[type]) {
          acc[type] = [];
        }
        acc?.[type]?.push({
          accountCode: account?.account_code,
          accountName: account?.account_name,
          accountType: account?.account_type,
          debitBalance: parseFloat(account?.debit_balance || 0),
          creditBalance: parseFloat(account?.credit_balance || 0),
          netBalance: parseFloat(account?.net_balance || 0),
          openingBalance: parseFloat(account?.opening_balance || 0),
          periodActivity: parseFloat(account?.period_activity || 0),
          source: account?.data_source || 'journal_entries',
          expenseCount: parseInt(account?.expense_count || 0),
          billCount: parseInt(account?.bill_count || 0),
          isEnhancedWithExpenses: (account?.data_source || '')?.includes('transactions') || parseInt(account?.expense_count || 0) > 0
        });
        return acc;
      }, {});

      // Calculate enhanced type summaries
      const typeSummaries = Object.keys(accountsByType)?.reduce((acc, type) => {
        const typeAccounts = accountsByType?.[type] || [];
        acc[type] = {
          totalDebit: typeAccounts?.reduce((sum, acc) => sum + acc?.debitBalance, 0),
          totalCredit: typeAccounts?.reduce((sum, acc) => sum + acc?.creditBalance, 0),
          accountCount: typeAccounts?.length,
          totalActivity: typeAccounts?.reduce((sum, acc) => sum + acc?.periodActivity, 0),
          expenseCount: typeAccounts?.reduce((sum, acc) => sum + acc?.expenseCount, 0),
          billCount: typeAccounts?.reduce((sum, acc) => sum + acc?.billCount, 0),
          accountsWithExpenses: typeAccounts?.filter(acc => acc?.expenseCount > 0)?.length,
          accountsWithBills: typeAccounts?.filter(acc => acc?.billCount > 0)?.length
        };
        return acc;
      }, {});

      return {
        title: 'Trial Balance (Enhanced with Direct Expense Integration)',
        asOfDate,
        calculationMethod: useJournalCalculations ? 'Enhanced Journal Transactions + Direct Expenses + Bills' : 'Enhanced Current Balances + Direct Expenses',
        data: data?.map(account => ({
          accountCode: account?.account_code,
          accountName: account?.account_name,
          accountType: account?.account_type,
          debitBalance: parseFloat(account?.debit_balance || 0),
          creditBalance: parseFloat(account?.credit_balance || 0),
          netBalance: parseFloat(account?.net_balance || 0),
          openingBalance: parseFloat(account?.opening_balance || 0),
          periodActivity: parseFloat(account?.period_activity || 0),
          source: account?.data_source || 'journal_entries',
          expenseCount: parseInt(account?.expense_count || 0),
          billCount: parseInt(account?.bill_count || 0),
          isEnhancedWithExpenses: (account?.data_source || '')?.includes('transactions') || parseInt(account?.expense_count || 0) > 0
        })) || [],
        accountsByType,
        typeSummaries,
        totals: {
          totalDebits,
          totalCredits,
          totalNetActivity,
          isBalanced: Math.abs(totalDebits - totalCredits) < 0.01,
          difference: totalDebits - totalCredits,
          balanceFromSummary: summary?.is_balanced || false,
          totalAccounts: summary?.total_accounts || 0,
          accountsWithActivity: summary?.accounts_with_activity || 0,
          expenseAccountsCount: summary?.total_expense_accounts || 0,
          totalExpenseAmount: parseFloat(summary?.total_expense_amount || 0),
          billAccountsCount: summary?.total_bill_accounts || 0,
          totalBillAmount: parseFloat(summary?.total_bill_amount || 0),
          enhancedWithExpenseData: true,
          dataIntegrationStatus: summary?.data_integration_status || 'basic'
        },
        expenseDetails: expenseDetails?.map(detail => ({
          accountCode: detail?.account_code,
          accountName: detail?.account_name,
          totalExpenseAmount: parseFloat(detail?.total_expense_amount || 0),
          expenseCount: parseInt(detail?.expense_count || 0),
          averageExpense: parseFloat(detail?.average_expense || 0),
          latestExpenseDate: detail?.latest_expense_date,
          expenseCategories: detail?.expense_categories || []
        })) || [],
        validation: {
          totalExpensesInDatabase: validation?.total_expenses_in_table || 0,
          expensesWithAccountMapping: validation?.expenses_with_account_id || 0,
          expensesWithoutAccountMapping: validation?.expenses_without_account_id || 0,
          integrationCompleteness: validation?.expenses_without_account_id === 0 ? 'Complete' : 'Partial',
          recommendedActions: validation?.suggested_actions || 'No recommendations available'
        },
        metadata: {
          generatedAt: new Date()?.toISOString(),
          includesJournalTransactions: useJournalCalculations,
          includesDirectExpenseData: true,
          includesDirectBillData: true,
          expenseIntegrationMethod: 'direct_database_calculation',
          organizationId,
          enhancementLevel: 'comprehensive_expense_integration'
        }
      };
    } catch (error) {
      console.error('Error fetching enhanced trial balance data with direct expense integration:', error);
      throw error;
    }
  },

  async getProfitLossData(organizationId, filters) {
    try {
      const startDate = filters?.startDate || new Date(new Date().getFullYear(), 0, 1)?.toISOString()?.split('T')?.[0];
      const endDate = filters?.endDate || new Date()?.toISOString()?.split('T')?.[0];

      // Get enhanced P&L data with all account details
      const { data: accountDetails, error: accountError } = await supabase?.rpc('generate_profit_loss_with_account_details', {
        org_uuid: organizationId,
        start_date: startDate,
        end_date: endDate
      });

      if (accountError) {
        console.error('Error fetching P&L account details:', accountError);
        throw accountError;
      }

      // Get P&L summary metrics
      const { data: summaryData, error: summaryError } = await supabase?.rpc('get_profit_loss_summary', {
        org_uuid: organizationId,
        start_date: startDate,
        end_date: endDate
      });

      if (summaryError) {
        console.error('Error fetching P&L summary:', summaryError);
        throw summaryError;
      }

      const summary = summaryData?.[0] || {};

      // Group accounts by type for detailed display
      const revenueAccounts = accountDetails?.filter(acc => acc?.account_type === 'revenue')?.map(acc => ({
        accountCode: acc?.account_code,
        accountName: acc?.account_name,
        accountSubtype: acc?.account_subtype,
        actualAmount: parseFloat(acc?.actual_amount || 0),
        transactionCount: parseInt(acc?.transaction_count || 0),
        averageAmount: parseFloat(acc?.average_transaction_amount || 0),
        dataSources: acc?.data_sources || [],
        percentageOfTotal: parseFloat(acc?.percentage_of_total || 0),
        ifrsSection: acc?.ifrs_section,
        ifrsSubsection: acc?.ifrs_subsection,
        openingBalance: parseFloat(acc?.opening_balance || 0),
        periodActivity: parseFloat(acc?.period_activity || 0),
        closingBalance: parseFloat(acc?.closing_balance || 0)
      })) || [];

      const expenseAccounts = accountDetails?.filter(acc => acc?.account_type === 'expense')?.map(acc => ({
        accountCode: acc?.account_code,
        accountName: acc?.account_name,
        accountSubtype: acc?.account_subtype,
        actualAmount: parseFloat(acc?.actual_amount || 0),
        transactionCount: parseInt(acc?.transaction_count || 0),
        averageAmount: parseFloat(acc?.average_transaction_amount || 0),
        dataSources: acc?.data_sources || [],
        percentageOfTotal: parseFloat(acc?.percentage_of_total || 0),
        ifrsSection: acc?.ifrs_section,
        ifrsSubsection: acc?.ifrs_subsection,
        openingBalance: parseFloat(acc?.opening_balance || 0),
        periodActivity: parseFloat(acc?.period_activity || 0),
        closingBalance: parseFloat(acc?.closing_balance || 0)
      })) || [];

      // Create revenue breakdown by account name
      const revenueBreakdown = revenueAccounts?.reduce((acc, account) => {
        acc[account?.accountName] = account?.actualAmount;
        return acc;
      }, {});

      // Create expense breakdown by account name  
      const expenseBreakdown = expenseAccounts?.reduce((acc, account) => {
        acc[account?.accountName] = account?.actualAmount;
        return acc;
      }, {});

      // Calculate enhanced totals
      const totalRevenue = revenueAccounts?.reduce((sum, acc) => sum + acc?.actualAmount, 0) || 0;
      const totalExpenses = expenseAccounts?.reduce((sum, acc) => sum + acc?.actualAmount, 0) || 0;

      return {
        title: 'Profit & Loss Statement (Enhanced with Account Details)',
        period: {
          startDate,
          endDate
        },
        revenue: {
          totalRevenue,
          breakdown: revenueBreakdown,
          accounts: revenueAccounts,
          accountCount: revenueAccounts?.length || 0,
          transactionCount: summary?.total_revenue_transactions || 0,
          largestAccount: summary?.largest_revenue_account || 'None'
        },
        expenses: {
          totalExpenses,
          breakdown: expenseBreakdown,
          accounts: expenseAccounts,
          accountCount: expenseAccounts?.length || 0,
          transactionCount: summary?.total_expense_transactions || 0,
          largestAccount: summary?.largest_expense_account || 'None'
        },
        profitability: {
          grossProfit: parseFloat(summary?.gross_profit || totalRevenue),
          operatingIncome: parseFloat(summary?.operating_income || (totalRevenue - totalExpenses)),
          netIncome: parseFloat(summary?.net_income || (totalRevenue - totalExpenses)),
          grossProfitMargin: parseFloat(summary?.gross_profit_margin || 0),
          operatingMargin: parseFloat(summary?.operating_margin || 0),
          netProfitMargin: parseFloat(summary?.net_profit_margin || 0)
        },
        summary: {
          totalRevenue,
          totalExpenses,
          netIncome: totalRevenue - totalExpenses,
          isProfitable: (totalRevenue - totalExpenses) > 0,
          calculationMethod: 'enhanced_account_level_with_actual_values',
          revenueAccountCount: revenueAccounts?.length || 0,
          expenseAccountCount: expenseAccounts?.length || 0,
          dataIntegrationSources: ['invoices', 'expenses', 'bills', 'journal_entries']
        },
        accountDetails: {
          revenue: revenueAccounts,
          expense: expenseAccounts,
          totalAccounts: (revenueAccounts?.length || 0) + (expenseAccounts?.length || 0)
        },
        metadata: {
          generatedAt: new Date()?.toISOString(),
          enhancementLevel: 'comprehensive_account_breakdown',
          includesActualAccountValues: true,
          accountLevelDetailAvailable: true,
          organizationId
        }
      };
    } catch (error) {
      console.error('Error fetching enhanced profit & loss data:', error);
      throw error;
    }
  },

  async getBalanceSheetData(organizationId, filters) {
    try {
      const asOfDate = filters?.asOfDate || new Date()?.toISOString()?.split('T')?.[0];

      // Get all accounts with their current balances
      const { data: accounts } = await supabase?.from('chart_of_accounts')?.select('*')?.eq('organization_id', organizationId)?.eq('status', 'active');

      // Get account balances from account_balances table or current_balance field
      const { data: accountBalances } = await supabase?.from('account_balances')?.select('*')?.eq('organization_id', organizationId)?.lte('period_end_date', asOfDate)?.order('period_end_date', { ascending: false });

      // Group accounts by type and subtype
      const accountsByType = accounts?.reduce((acc, account) => {
        const type = account?.account_type;
        const subtype = account?.account_subtype;
        
        if (!acc?.[type]) {
          acc[type] = {};
        }
        if (!acc?.[type]?.[subtype]) {
          acc[type][subtype] = [];
        }

        // Get the most recent balance for this account
        const recentBalance = accountBalances?.find(bal => bal?.account_id === account?.id);
        const balance = recentBalance ? parseFloat(recentBalance?.closing_balance || 0) : parseFloat(account?.current_balance || 0);

        acc?.[type]?.[subtype]?.push({
          accountCode: account?.account_code,
          accountName: account?.account_name,
          balance: balance,
          normalBalance: account?.normal_balance
        });

        return acc;
      }, {});

      // Calculate totals for each section
      const calculateTypeTotal = (type) => {
        if (!accountsByType?.[type]) return 0;
        return Object.values(accountsByType?.[type])?.flat()?.reduce((sum, account) => sum + account?.balance, 0);
      };

      const totalAssets = calculateTypeTotal('asset');
      const totalLiabilities = calculateTypeTotal('liability');
      const totalEquity = calculateTypeTotal('equity');

      // Verify accounting equation: Assets = Liabilities + Equity
      const isBalanced = Math.abs(totalAssets - (totalLiabilities + totalEquity)) < 0.01;

      return {
        title: 'Balance Sheet',
        asOfDate,
        assets: {
          currentAssets: accountsByType?.asset?.current_asset || [],
          nonCurrentAssets: accountsByType?.asset?.non_current_asset || [],
          fixedAssets: accountsByType?.asset?.fixed_asset || [],
          intangibleAssets: accountsByType?.asset?.intangible_asset || [],
          totalAssets
        },
        liabilities: {
          currentLiabilities: accountsByType?.liability?.current_liability || [],
          nonCurrentLiabilities: accountsByType?.liability?.non_current_liability || [],
          longTermDebt: accountsByType?.liability?.long_term_debt || [],
          totalLiabilities
        },
        equity: {
          shareCapital: accountsByType?.equity?.share_capital || [],
          retainedEarnings: accountsByType?.equity?.retained_earnings || [],
          otherEquity: accountsByType?.equity?.other_equity || [],
          totalEquity
        },
        totals: {
          totalAssets,
          totalLiabilities,
          totalEquity,
          totalLiabilitiesAndEquity: totalLiabilities + totalEquity,
          isBalanced,
          difference: totalAssets - (totalLiabilities + totalEquity)
        }
      };
    } catch (error) {
      console.error('Error fetching balance sheet data:', error);
      throw error;
    }
  },

  async getInvoiceAgingData(organizationId, filters) {
    try {
      // Get invoices with aging information - similar to outstanding balances but focused on aging
      const { data: invoices, error } = await supabase?.from('invoices')?.select(`
          *,
          customers (
            customer_name,
            customer_number,
            email
          )
        `)?.eq('organization_id', organizationId)?.order('issue_date', { ascending: false });

      if (error) throw error;

      const agingData = invoices?.map(invoice => {
        const totalAmount = parseFloat(invoice?.total_amount || 0);
        const amountPaid = parseFloat(invoice?.amount_paid || 0);
        const balanceDue = totalAmount - amountPaid;
        const daysFromIssue = invoice?.issue_date 
          ? Math.floor((new Date() - new Date(invoice.issue_date)) / (1000 * 60 * 60 * 24))
          : 0;

        return {
          invoiceNumber: invoice?.invoice_number,
          customerName: invoice?.customers?.customer_name || 'Unknown Customer',
          issueDate: invoice?.issue_date,
          dueDate: invoice?.due_date,
          totalAmount,
          amountPaid,
          balanceDue,
          daysFromIssue,
          status: invoice?.status,
          agingBucket: daysFromIssue <= 30 ? '0-30 Days' :
                      daysFromIssue <= 60 ? '31-60 Days' :
                      daysFromIssue <= 90 ? '61-90 Days' :
                      daysFromIssue <= 120 ? '91-120 Days' : '120+ Days'
        };
      }) || [];

      return {
        title: 'Invoice Aging Report',
        data: agingData,
        aging: {
          days0to30: agingData?.filter(inv => inv?.daysFromIssue <= 30)?.reduce((sum, inv) => sum + inv?.totalAmount, 0),
          days31to60: agingData?.filter(inv => inv?.daysFromIssue > 30 && inv?.daysFromIssue <= 60)?.reduce((sum, inv) => sum + inv?.totalAmount, 0),
          days61to90: agingData?.filter(inv => inv?.daysFromIssue > 60 && inv?.daysFromIssue <= 90)?.reduce((sum, inv) => sum + inv?.totalAmount, 0),
          days91to120: agingData?.filter(inv => inv?.daysFromIssue > 90 && inv?.daysFromIssue <= 120)?.reduce((sum, inv) => sum + inv?.totalAmount, 0),
          days120plus: agingData?.filter(inv => inv?.daysFromIssue > 120)?.reduce((sum, inv) => sum + inv?.totalAmount, 0)
        },
        totals: {
          totalInvoices: agingData?.length,
          totalAmount: agingData?.reduce((sum, inv) => sum + inv?.totalAmount, 0)
        }
      };
    } catch (error) {
      console.error('Error fetching invoice aging data:', error);
      throw error;
    }
  },

  async getCustomerRevenueData(organizationId, filters) {
    try {
      // Validate prerequisites
      const { data: validCustomers } = await supabase?.from('customers')?.select('id, customer_name')?.eq('organization_id', organizationId)?.not('customer_name', 'is', null)?.neq('customer_name', '');

      if (!validCustomers || validCustomers?.length === 0) {
        throw new Error('Cannot generate customer revenue analysis: No customers with valid names found');
      }

      const { data: invoices, error } = await supabase?.from('invoices')?.select(`
          *,
          customers!inner (
            customer_name,
            customer_number,
            email
          )
        `)?.eq('organization_id', organizationId)?.eq('status', 'paid')?.not('customers.customer_name', 'is', null)?.neq('customers.customer_name', '');

      if (error) throw error;

      if (!invoices || invoices?.length === 0) {
        return {
          title: 'Customer Revenue Analysis',
          data: [],
          totals: {
            totalCustomers: 0,
            totalRevenue: 0,
            averageRevenuePerCustomer: 0
          },
          validationMessage: 'No paid invoices with valid customer data found'
        };
      }

      const customerRevenue = invoices?.reduce((acc, invoice) => {
        const customerName = invoice?.customers?.customer_name || 'Unknown Customer';
        const amount = parseFloat(invoice?.total_amount || 0);
        
        if (!acc?.[customerName]) {
          acc[customerName] = {
            customerName,
            totalRevenue: 0,
            invoiceCount: 0,
            averageInvoice: 0
          };
        }
        
        acc[customerName].totalRevenue += amount;
        acc[customerName].invoiceCount += 1;
        acc[customerName].averageInvoice = acc?.[customerName]?.totalRevenue / acc?.[customerName]?.invoiceCount;
        
        return acc;
      }, {});

      const revenueData = Object.values(customerRevenue || {})?.sort((a, b) => b?.totalRevenue - a?.totalRevenue);

      return {
        title: 'Customer Revenue Analysis (Validated Data)',
        data: revenueData,
        totals: {
          totalCustomers: revenueData?.length,
          totalRevenue: revenueData?.reduce((sum, customer) => sum + customer?.totalRevenue, 0),
          averageRevenuePerCustomer: revenueData?.length > 0 
            ? revenueData?.reduce((sum, customer) => sum + customer?.totalRevenue, 0) / revenueData?.length 
            : 0
        },
        validationInfo: {
          totalValidCustomersInSystem: validCustomers?.length,
          customersWithRevenue: revenueData?.length,
          paidInvoicesAnalyzed: invoices?.length
        }
      };
    } catch (error) {
      console.error('Error fetching customer revenue data:', error);
      throw error;
    }
  },

  async getExpenseCategoriesData(organizationId, filters) {
    try {
      const { data: expenses, error } = await supabase?.from('expenses')?.select('*')?.eq('organization_id', organizationId);

      if (error) throw error;

      const categoryBreakdown = expenses?.reduce((acc, expense) => {
        const category = expense?.category || 'Uncategorized';
        const amount = parseFloat(expense?.amount || 0);
        
        if (!acc?.[category]) {
          acc[category] = {
            category,
            totalAmount: 0,
            expenseCount: 0,
            averageExpense: 0
          };
        }
        
        acc[category].totalAmount += amount;
        acc[category].expenseCount += 1;
        acc[category].averageExpense = acc?.[category]?.totalAmount / acc?.[category]?.expenseCount;
        
        return acc;
      }, {});

      const categoryData = Object.values(categoryBreakdown || {})?.sort((a, b) => b?.totalAmount - a?.totalAmount);

      return {
        title: 'Expense Categories Report',
        data: categoryData,
        totals: {
          totalCategories: categoryData?.length,
          totalExpenses: categoryData?.reduce((sum, cat) => sum + cat?.totalAmount, 0),
          averagePerCategory: categoryData?.length > 0 
            ? categoryData?.reduce((sum, cat) => sum + cat?.totalAmount, 0) / categoryData?.length 
            : 0
        }
      };
    } catch (error) {
      console.error('Error fetching expense categories data:', error);
      throw error;
    }
  },

  async getVendorSpendingData(organizationId, filters) {
    try {
      const { data: expenses, error: expError } = await supabase?.from('expenses')?.select(`
          *,
          vendors (
            vendor_name,
            vendor_number
          )
        `)?.eq('organization_id', organizationId);

      const { data: bills, error: billError } = await supabase?.from('bills')?.select(`
          *,
          vendors (
            vendor_name,
            vendor_number
          )
        `)?.eq('organization_id', organizationId);

      if (expError) throw expError;
      if (billError) throw billError;

      const vendorSpending = {};

      // Process expenses
      expenses?.forEach(expense => {
        const vendorName = expense?.vendors?.vendor_name || 'Unknown Vendor';
        const amount = parseFloat(expense?.amount || 0);
        
        if (!vendorSpending?.[vendorName]) {
          vendorSpending[vendorName] = {
            vendorName,
            totalSpending: 0,
            expenseCount: 0,
            billCount: 0,
            averageTransaction: 0
          };
        }
        
        vendorSpending[vendorName].totalSpending += amount;
        vendorSpending[vendorName].expenseCount += 1;
      });

      // Process bills
      bills?.forEach(bill => {
        const vendorName = bill?.vendors?.vendor_name || 'Unknown Vendor';
        const amount = parseFloat(bill?.total_amount || 0);
        
        if (!vendorSpending?.[vendorName]) {
          vendorSpending[vendorName] = {
            vendorName,
            totalSpending: 0,
            expenseCount: 0,
            billCount: 0,
            averageTransaction: 0
          };
        }
        
        vendorSpending[vendorName].totalSpending += amount;
        vendorSpending[vendorName].billCount += 1;
      });

      // Calculate averages
      Object.values(vendorSpending)?.forEach(vendor => {
        const totalTransactions = vendor?.expenseCount + vendor?.billCount;
        vendor.averageTransaction = totalTransactions > 0 ? vendor?.totalSpending / totalTransactions : 0;
      });

      const spendingData = Object.values(vendorSpending)?.sort((a, b) => b?.totalSpending - a?.totalSpending);

      return {
        title: 'Vendor Spending Analysis',
        data: spendingData,
        totals: {
          totalVendors: spendingData?.length,
          totalSpending: spendingData?.reduce((sum, vendor) => sum + vendor?.totalSpending, 0),
          averageSpendingPerVendor: spendingData?.length > 0 
            ? spendingData?.reduce((sum, vendor) => sum + vendor?.totalSpending, 0) / spendingData?.length 
            : 0
        }
      };
    } catch (error) {
      console.error('Error fetching vendor spending data:', error);
      throw error;
    }
  },

  async getPaymentTrendsData(organizationId, filters) {
    try {
      const { data: paymentsReceived, error: receivedError } = await supabase?.from('payments_received')?.select('*')?.eq('organization_id', organizationId)?.order('payment_date', { ascending: false });

      const { data: paymentsMade, error: madeError } = await supabase?.from('payments_made')?.select('*')?.eq('organization_id', organizationId)?.order('payment_date', { ascending: false });

      if (receivedError) throw receivedError;
      if (madeError) throw madeError;

      // Group payments by month
      const monthlyTrends = {};

      paymentsReceived?.forEach(payment => {
        const month = new Date(payment?.payment_date)?.toISOString()?.substring(0, 7); // YYYY-MM format
        if (!monthlyTrends?.[month]) {
          monthlyTrends[month] = { month, received: 0, made: 0, net: 0 };
        }
        monthlyTrends[month].received += parseFloat(payment?.amount_received || 0);
      });

      paymentsMade?.forEach(payment => {
        const month = new Date(payment?.payment_date)?.toISOString()?.substring(0, 7); // YYYY-MM format
        if (!monthlyTrends?.[month]) {
          monthlyTrends[month] = { month, received: 0, made: 0, net: 0 };
        }
        monthlyTrends[month].made += parseFloat(payment?.amount_paid || 0);
      });

      // Calculate net for each month
      Object.values(monthlyTrends)?.forEach(trend => {
        trend.net = trend?.received - trend?.made;
      });

      const trendsData = Object.values(monthlyTrends)?.sort((a, b) => a?.month?.localeCompare(b?.month));

      return {
        title: 'Payment Trends Analysis',
        data: trendsData,
        summary: {
          totalMonths: trendsData?.length,
          totalReceived: trendsData?.reduce((sum, trend) => sum + trend?.received, 0),
          totalMade: trendsData?.reduce((sum, trend) => sum + trend?.made, 0),
          averageMonthlyNet: trendsData?.length > 0 
            ? trendsData?.reduce((sum, trend) => sum + trend?.net, 0) / trendsData?.length 
            : 0
        }
      };
    } catch (error) {
      console.error('Error fetching payment trends data:', error);
      throw error;
    }
  },

  // Get recent reports accessed by user
  async getRecentReports(organizationId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      // Check if revenue data setup is valid before returning revenue-related reports
      const { data: validCustomers } = await supabase?.from('customers')?.select('id')?.eq('organization_id', organizationId)?.not('customer_name', 'is', null)?.neq('customer_name', '');
      const { data: revenueAccounts } = await supabase?.from('chart_of_accounts')?.select('id')?.eq('organization_id', organizationId)?.eq('account_type', 'revenue')?.eq('status', 'active')?.not('account_subtype', 'is', null);

      const hasValidRevenueSetup = validCustomers && validCustomers?.length > 0 && revenueAccounts && revenueAccounts?.length > 0;

      // Query actual user report access history from database
      const { data: reportAccess, error } = await supabase
        ?.from('user_report_access')
        ?.select('report_id, report_name, report_type, last_accessed_at, access_count')
        ?.eq('organization_id', organizationId)
        ?.eq('user_id', user?.id)
        ?.order('last_accessed_at', { ascending: false })
        ?.limit(5);

      // If no access history table exists or no data, return empty array
      if (error || !reportAccess || reportAccess?.length === 0) {
        console.log('No recent report access history found');
        return [];
      }

      // Filter out revenue reports if setup is not valid
      const filteredReports = reportAccess?.filter(access => {
        if (access?.report_type === 'revenue' && !hasValidRevenueSetup) {
          return false; // Exclude revenue reports if setup is invalid
        }
        return true;
      });

      // Transform database results into component format
      return filteredReports?.map(access => ({
        id: access?.report_id,
        name: access?.report_name,
        type: access?.report_type,
        lastAccessed: access?.last_accessed_at,
        description: `Accessed ${access?.access_count || 1} time${access?.access_count !== 1 ? 's' : ''}`,
        isEnabled: access?.report_type !== 'revenue' || hasValidRevenueSetup
      }));
    } catch (error) {
      console.error('Error fetching recent reports:', error);
      return []; // Return empty array instead of throwing error
    }
  },

  // New method to get detailed account balance information
  async getAccountBalanceDetails(accountId, organizationId, asOfDate) {
    try {
      const { data, error } = await supabase?.rpc('get_account_balance_details', {
        account_uuid: accountId,
        org_uuid: organizationId,
        as_of_date: asOfDate || new Date()?.toISOString()?.split('T')?.[0]
      });

      if (error) throw error;

      const accountDetails = data?.[0];
      if (!accountDetails) {
        throw new Error('Account not found or no access');
      }

      return {
        accountCode: accountDetails?.account_code,
        accountName: accountDetails?.account_name,
        accountType: accountDetails?.account_type,
        normalBalance: accountDetails?.normal_balance,
        openingBalance: parseFloat(accountDetails?.opening_balance || 0),
        totalDebits: parseFloat(accountDetails?.total_debits || 0),
        totalCredits: parseFloat(accountDetails?.total_credits || 0),
        netActivity: parseFloat(accountDetails?.net_activity || 0),
        closingBalance: parseFloat(accountDetails?.closing_balance || 0),
        transactionCount: parseInt(accountDetails?.transaction_count || 0)
      };
    } catch (error) {
      console.error('Error fetching account balance details:', error);
      throw error;
    }
  },

  // Enhanced method to get comparative trial balance (current vs previous period)
  async getComparativeTrialBalance(organizationId, currentDate, previousDate) {
    try {
      // Get current period trial balance
      const currentTB = await this.getTrialBalanceData(organizationId, { 
        asOfDate: currentDate,
        useJournalCalculations: true 
      });
      
      // Get previous period trial balance
      const previousTB = await this.getTrialBalanceData(organizationId, { 
        asOfDate: previousDate,
        useJournalCalculations: true 
      });

      // Create comparison data
      const accountComparisons = currentTB?.data?.map(current => {
        const previous = previousTB?.data?.find(prev => prev?.accountCode === current?.accountCode);
        return {
          ...current,
          previousDebitBalance: previous?.debitBalance || 0,
          previousCreditBalance: previous?.creditBalance || 0,
          debitChange: current?.debitBalance - (previous?.debitBalance || 0),
          creditChange: current?.creditBalance - (previous?.creditBalance || 0),
          netChange: current?.netBalance - (previous?.netBalance || 0)
        };
      }) || [];

      return {
        title: 'Comparative Trial Balance',
        currentPeriod: {
          date: currentDate,
          ...currentTB
        },
        previousPeriod: {
          date: previousDate,
          ...previousTB
        },
        comparison: {
          data: accountComparisons,
          totals: {
            currentDebits: currentTB?.totals?.totalDebits || 0,
            currentCredits: currentTB?.totals?.totalCredits || 0,
            previousDebits: previousTB?.totals?.totalDebits || 0,
            previousCredits: previousTB?.totals?.totalCredits || 0,
            debitChange: (currentTB?.totals?.totalDebits || 0) - (previousTB?.totals?.totalDebits || 0),
            creditChange: (currentTB?.totals?.totalCredits || 0) - (previousTB?.totals?.totalCredits || 0)
          }
        }
      };
    } catch (error) {
      console.error('Error fetching comparative trial balance data:', error);
      throw error;
    }
  }
};